import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import {
  fetchAllPortfolioAssets,
  PORTFOLIO_ASSETS
} from '../services/portfolioService';
import {
  normalizeSeriesToIndex,
  calculateAssetStatistics,
  buildCorrelationMatrix,
  simulatePortfolio,
  toPercentageDisplay
} from '../utils/portfolioAnalytics';

const DEFAULT_WEIGHTS = {
  gold: 40,
  stocks: 40,
  bitcoin: 20
};

const PortfolioAnalysisPage = () => {
  const [assetSeries, setAssetSeries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadPortfolioData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPortfolioData = async () => {
    setLoading(true);
    setError(null);

    try {
      const results = await fetchAllPortfolioAssets({ range: '3y', interval: '1d' });
      const nextSeries = {};
      const errors = [];

      results.forEach((item) => {
        if (item.error) {
          errors.push(`${item.asset.name}: ${item.error.message}`);
        } else if (item.series?.length) {
          nextSeries[item.asset.id] = {
            asset: item.asset,
            series: item.series
          };
        }
      });

      if (errors.length) {
        setError(
          `Some assets failed to load: ${errors.join(' | ')}. Try refreshing in a moment.`
        );
      }

      setAssetSeries(nextSeries);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Portfolio analysis load error:', err);
      setError(
        err.message ||
          'Unable to load portfolio data. Please confirm the backend proxy is running.'
      );
    } finally {
      setLoading(false);
    }
  };

  const normalizedSeriesMap = useMemo(() => {
    const map = {};
    Object.entries(assetSeries).forEach(([id, value]) => {
      map[id] = normalizeSeriesToIndex(value.series);
    });
    return map;
  }, [assetSeries]);

  const chartData = useMemo(() => {
    const timestampSet = new Set();
    const seriesMaps = {};

    Object.entries(normalizedSeriesMap).forEach(([id, series]) => {
      const lookup = new Map(series.map((point) => [point.timestamp, point.indexValue]));
      seriesMaps[id] = lookup;
      series.forEach((point) => {
        timestampSet.add(point.timestamp);
      });
    });

    const sortedTimestamps = Array.from(timestampSet).sort((a, b) => a - b);

    return sortedTimestamps.map((timestamp) => {
      const row = {
        timestamp,
        dateLabel: new Date(timestamp).toISOString()
      };

      Object.keys(seriesMaps).forEach((id) => {
        const value = seriesMaps[id].get(timestamp);
        if (value !== undefined) {
          row[id] = Number(value.toFixed(2));
        }
      });

      return row;
    });
  }, [normalizedSeriesMap]);

  const assetStats = useMemo(() => {
    const stats = {};
    Object.entries(assetSeries).forEach(([id, value]) => {
      stats[id] = calculateAssetStatistics(value.series);
    });
    return stats;
  }, [assetSeries]);

  const correlationMatrix = useMemo(() => {
    const seriesMap = {};
    Object.entries(assetSeries).forEach(([id, value]) => {
      seriesMap[id] = value.series;
    });

    if (Object.keys(seriesMap).length === 0) {
      return {};
    }

    return buildCorrelationMatrix(seriesMap);
  }, [assetSeries]);

  const portfolioSummary = useMemo(() => {
    const seriesMap = {};
    Object.entries(assetSeries).forEach(([id, value]) => {
      seriesMap[id] = value.series;
    });

    if (Object.keys(seriesMap).length === 0) {
      return null;
    }

    const weightDecimals = Object.fromEntries(
      Object.entries(weights).map(([id, pct]) => [id, (pct || 0) / 100])
    );

    return simulatePortfolio(seriesMap, weightDecimals);
  }, [assetSeries, weights]);

  const weightSum = useMemo(
    () =>
      Object.values(weights).reduce((acc, value) => acc + (Number(value) || 0), 0),
    [weights]
  );

  const handleWeightChange = (assetId, value) => {
    const numeric = Number(value);

    setWeights((prev) => ({
      ...prev,
      [assetId]: Number.isFinite(numeric) && numeric >= 0 ? numeric : 0
    }));
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading 3-year history…</span>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className="py-12 text-center text-gray-500 text-sm">
          Historical data not available yet. Try refreshing in a moment.
        </div>
      );
    }

    return (
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="timestamp"
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString('en-US', {
                  month: 'short',
                  year: '2-digit'
                })
              }
              stroke="#9ca3af"
            />
            <YAxis
              stroke="#9ca3af"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) =>
                `${value.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}`
              }
            />
            <Tooltip
              labelFormatter={(value) =>
                new Date(value).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })
              }
              formatter={(value, key) => {
                const asset = PORTFOLIO_ASSETS.find((item) => item.id === key);
                return [
                  `${Number(value).toFixed(2)}`,
                  asset ? `${asset.name} (Index)` : key
                ];
              }}
            />
            <Legend />
            {PORTFOLIO_ASSETS.map((asset) => (
              <Line
                key={asset.id}
                type="monotone"
                dataKey={asset.id}
                stroke={asset.color}
                dot={false}
                strokeWidth={2}
                name={asset.name}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderStatsTable = () => {
    if (!Object.keys(assetStats).length) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Asset
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Avg Return (Annual)
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Volatility (Annual)
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Max Drawdown
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {PORTFOLIO_ASSETS.map((asset) => {
              const stats = assetStats[asset.id];
              return (
                <tr key={asset.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{asset.name}</div>
                    <div className="text-xs text-gray-500">{asset.description}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                    {toPercentageDisplay(stats?.averageReturn)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                    {toPercentageDisplay(stats?.volatility)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                    {toPercentageDisplay(stats?.maxDrawdown)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCorrelationMatrix = () => {
    const assetIds = PORTFOLIO_ASSETS.map((asset) => asset.id);
    if (!assetIds.length) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Asset
              </th>
              {assetIds.map((id) => (
                <th
                  key={`header-${id}`}
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  {PORTFOLIO_ASSETS.find((asset) => asset.id === id)?.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {assetIds.map((rowId) => (
              <tr key={`row-${rowId}`}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {PORTFOLIO_ASSETS.find((asset) => asset.id === rowId)?.name}
                </td>
                {assetIds.map((columnId) => {
                  const value = correlationMatrix[rowId]?.[columnId];
                  const display =
                    value === null || value === undefined
                      ? 'N/A'
                      : value.toFixed(2);
                  return (
                    <td
                      key={`cell-${rowId}-${columnId}`}
                      className="px-4 py-3 text-right text-sm text-gray-900"
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderAllocationSimulator = () => {
    const normalizedWeights = portfolioSummary?.normalizedWeights || {};

    return (
      <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Allocation Simulator
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Enter weights (0-100). We normalize them automatically if the total is not exactly
            100%.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {PORTFOLIO_ASSETS.map((asset) => (
            <label key={`weight-${asset.id}`} className="block">
              <span className="text-sm font-medium text-gray-700">
                {asset.name}
              </span>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={weights[asset.id] ?? 0}
                onChange={(event) => handleWeightChange(asset.id, event.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="mt-1 block text-xs text-gray-400">
                Normalized: {(normalizedWeights[asset.id] * 100 || 0).toFixed(1)}%
              </span>
            </label>
          ))}
        </div>

        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
          <p>
            Entered weights sum to <span className="font-semibold">{weightSum.toFixed(1)}%</span>.
            We rescale them to 100% before calculations so the mix remains valid.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Expected Return (Annual)
            </p>
            <p className="mt-2 text-xl font-semibold text-gray-900">
              {toPercentageDisplay(portfolioSummary?.expectedReturn)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Volatility (Annual)
            </p>
            <p className="mt-2 text-xl font-semibold text-gray-900">
              {toPercentageDisplay(portfolioSummary?.volatility)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Sharpe (no risk-free)
            </p>
            <p className="mt-2 text-xl font-semibold text-gray-900">
              {portfolioSummary?.sharpeRatio === null ||
              portfolioSummary?.sharpeRatio === undefined
                ? 'N/A'
                : portfolioSummary.sharpeRatio.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">3-Year Portfolio Checkup</h1>
          <p className="mt-1 text-sm text-gray-500">
            Compare gold, US stocks, and bitcoin over the last three years. Explore correlations and
            run quick allocation scenarios.
          </p>
          {lastUpdated && (
            <p className="mt-1 text-xs text-gray-400">
              Updated {lastUpdated.toLocaleString('en-US')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={loadPortfolioData}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          <AlertCircle className="mt-0.5 h-5 w-5" />
          <div>{error}</div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Indexed Performance (Base = 100)</h2>
        {renderChart()}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {renderStatsTable()}
        {renderCorrelationMatrix()}
      </div>

      {renderAllocationSimulator()}

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900">Quick Notes</h3>
        <ul className="mt-3 space-y-2 text-sm text-gray-600">
          <li>
            Data sources: Yahoo Finance via our proxy (`GC=F`, `^GSPC`, `BTC-USD`). Free tier refresh
            cadence may lag intraday by up to 15 minutes.
          </li>
          <li>
            Returns and volatility annualized assuming 252 trading days. Sharpe ratio uses a zero
            risk-free rate for simplicity.
          </li>
          <li>
            Try adjusting allocations to test defensive vs growth mixes. Enter any weights; we
            normalize automatically so you can iterate quickly.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PortfolioAnalysisPage;












